import React, { useRef } from "react";
import { createPortal } from "react-dom";

import { useIsomorphicLayoutEffect } from "shared/hooks/useIsomorphicLayoutEffect";

type PortalProps = {
  appendTo?: HTMLElement;
  children: React.ReactElement;
  isFixedBody?: boolean;
};

const DEFAULT_Z_INDEX = 1000;
let portalAmount = DEFAULT_Z_INDEX;

const Portal: React.FC<PortalProps> = ({
  children,
  isFixedBody = true,
  appendTo = document.querySelector("#portal-root") as HTMLElement,
}) => {
  const portalRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (isFixedBody) {
      document.querySelectorAll(".portal-padding, body").forEach((node) => {
        const element = node as HTMLElement;
        // Skip padding-right compensation when body overflow is already hidden.
        // window.innerWidth - body.clientWidth is meant to measure the scrollbar width,
        // but when overflow:hidden is already applied (e.g. by SparkStories on mobile),
        // the body layout is in a modified state and the calculation can produce
        // a false value (e.g. 289px instead of 0).
        // This only affects pages that set body overflow:hidden before a Portal opens —
        // on all other pages body is scrollable and the calculation is correct.
        if (window.getComputedStyle(document.body).overflow === "hidden") {
          return;
        }
        element.style.setProperty(
          "padding-right",
          `${window.innerWidth - document.body.clientWidth}px`
        );
      });
      document.querySelectorAll("body").forEach((node) => {
        node.classList.add("scroll-disabled");
      });
    }
    portalAmount += 10;
    if (portalRef.current && portalRef.current.lastElementChild) {
      const lastElementChild = portalRef.current
        .lastElementChild as HTMLElement;
      lastElementChild.style.zIndex = String(portalAmount);
    }
    return (): void => {
      portalAmount -= 10;
      if (isFixedBody && portalAmount === DEFAULT_Z_INDEX) {
        document.querySelectorAll("body").forEach((node) => {
          node.classList.remove("scroll-disabled");
        });
        document.querySelectorAll(".portal-padding, body").forEach((node) => {
          const element = node as HTMLElement;
          element.style.removeProperty("padding-right");
        });
      }
    };
  }, [appendTo, portalRef]);

  return <>{createPortal(<div ref={portalRef}>{children}</div>, appendTo)}</>;
};

export default Portal;
